/**
 * 🔒 A message a person reads is a sentence (decision 0455): it opens on a
 * capital and it closes on a stop. Every refusal in this application is
 * written in the service that raises it, in the lower-case, no-stop style the
 * plan itself is written in — *"a payment moves an amount above zero, in whole
 * cents"* — and that style reads as a fragment the moment it is shown to
 * somebody in a red banner.
 *
 * 🔨 The shaping happens once, where a refusal becomes an HTTP answer, and
 * never at the hundreds of places one is raised. A service's own message stays
 * what it was, so a suite asserting it is unaffected and the log keeps the
 * wording the code uses.
 */

/** A message that already ends a sentence, or ends in something a stop would spoil. */
const CLOSED = /[.!?:;)\]}”"'`]$/u;

/**
 * 🔨 A message opening on an identifier is left alone rather than capitalised.
 * `pricing.laborModifierComposition` becoming `Pricing.laborModifierComposition`
 * is a sentence about nothing: the fix for that message is to stop showing the
 * identifier, not to capitalise it.
 */
const OPENS_ON_IDENTIFIER = /^(?:[a-z][A-Za-z0-9]*\.[a-z]|[`'"“0-9])/u;

/**
 * 🔨 Retired 2026-09-28 (decision 1175, T420): a message that is ONLY a record
 * type and an id used to be left alone rather than punctuated, on the reading
 * that a full stop on a bare id does not make it a sentence. The owner's
 * stricter reading is the rule now — a subject, a verb and a complete thought
 * — so the source itself no longer raises a bare `Kind ${id}`; every such
 * call site was reworded to *No sales order abc123 was found.* in the same
 * batch. This function no longer carries an exemption for the shape, so the
 * filter and the source agree.
 */

export function asSentence(message: string): string {
  const text = message.trim();
  if (!text) return text;
  if (OPENS_ON_IDENTIFIER.test(text)) return text;
  const opened = text[0]!.toUpperCase() + text.slice(1);
  return CLOSED.test(opened) ? opened : `${opened}.`;
}
